/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  dataFetcher, baseUrl, getToken, STRIPE_PUBLIC_KEY,
} from 'Api';
import styles from './index.module.css';

// Load Stripe once (null if no key configured for this env) — reused for the
// 3DS step, same pattern as MembershipCheckout's saved-card flow.
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

const money = (cents, currency = 'usd') => ((Number(cents) || 0) / 100)
  .toLocaleString('en-US', { style: 'currency', currency });
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const discountValue = (d, currency = 'usd') => {
  if (!d) return '';
  if (d.percent_off) return d.percent_off === 100 ? 'Free' : `${d.percent_off}% off`;
  if (d.amount_off) return `${money(d.amount_off, currency)} off`;
  return '';
};
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const cardLabel = (c) => (c ? `${titleCase(c.brand)} •••• ${c.last4}` : '');

// What the plan actually renews at, accounting for a coupon that survives the
// upgrade. A `forever` coupon applies to every renewal; `once` is gone by then;
// `repeating` is time-dependent, so we conservatively show the full price and
// leave the exact figure to the backend.
const renewalCents = (amountCents, d) => {
  if (!d || d.duration !== 'forever') return amountCents;
  if (d.percent_off) return Math.round(amountCents * (1 - d.percent_off / 100));
  if (d.amount_off) return Math.max(0, amountCents - d.amount_off);
  return amountCents;
};

// Upgrade flow: list higher plans (same billing interval — BE enforces it) →
// preview the prorated amount due now (credit for the current plan) → confirm →
// in-place subscription price swap, with a 3DS step only when required.
const MembershipUpgradeModal = ({ open, membership, onClose, onUpgraded }) => {
  const m = membership || {};
  const interval = m.billing_title || 'Annual';
  const curAmount = Number(m.amount_cents) || 0;

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [card, setCard] = useState(null); // default payment method that gets charged
  const [selected, setSelected] = useState(null); // { plan, pricing }
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');

  // (Re)load upgrade options + the card on file whenever the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    setSelected(null);
    setPreview(null);
    setError('');
    let active = true;
    (async () => {
      setLoadingPlans(true);
      try {
        const token = getToken();
        const opts = token ? { params: { token } } : undefined;
        const [plansRes, pmRes] = await Promise.all([
          axios.get(`${baseUrl}/v4/membership-plans`, opts),
          axios.get(`${baseUrl}/v4/membership/payment-methods`, opts).catch(() => null),
        ]);
        if (!active) return;
        const data = plansRes?.data;
        if (data?.success && Array.isArray(data.data)) {
          // Only same-interval plans priced ABOVE the current one are upgrades
          // (never the same or a lower plan).
          const eligible = data.data
            .map((p) => ({ plan: p, pricing: (p.pricing || []).find((pr) => pr.billing_title === interval) }))
            .filter((o) => o.pricing && o.pricing.id != null && Number(o.pricing.price_cents) > curAmount);
          setPlans(eligible);
        } else {
          setError('Could not load upgrade options.');
        }
        const cards = pmRes?.data?.data;
        if (Array.isArray(cards) && cards.length) {
          setCard(cards.find((c) => c.is_default) || cards[0]);
        }
      } catch (e) {
        if (active) setError('Could not load upgrade options.');
      } finally {
        if (active) setLoadingPlans(false);
      }
    })();
    return () => { active = false; };
  }, [open, interval]);

  const choosePlan = async (opt) => {
    setSelected(opt);
    setPreview(null);
    setError('');
    setPreviewing(true);
    try {
      const res = await dataFetcher('v4/membership/upgrade-preview', {
        membership_plan_price_id: opt.pricing.id,
      });
      if (res?.success && res?.data) setPreview(res.data);
      else setError(res?.message || 'Could not preview this upgrade.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not preview this upgrade.');
    } finally {
      setPreviewing(false);
    }
  };

  const confirmUpgrade = async () => {
    if (!selected) return;
    setUpgrading(true);
    setError('');
    try {
      const res = await dataFetcher('v4/membership/upgrade', {
        membership_plan_price_id: selected.pricing.id,
      });
      const d = res?.data || {};

      if (d.status === 'requires_action' && d.client_secret) {
        const stripe = await stripePromise;
        if (!stripe) { setError('Payments are not configured.'); setUpgrading(false); return; }
        const { error: authErr } = await stripe.handleNextAction({ clientSecret: d.client_secret });
        if (authErr) { setError(authErr.message || 'Card authentication failed.'); setUpgrading(false); return; }
        onUpgraded?.();
        return;
      }
      if (d.status === 'active') { onUpgraded?.(); return; }
      setError(res?.message || 'Upgrade failed — please try again.');
      setUpgrading(false);
    } catch (e) {
      // BE returns 409 with a message for same-plan / downgrade / interval issues.
      setError(e?.response?.data?.message || 'Upgrade failed — please try again.');
      setUpgrading(false);
    }
  };

  if (!open) return null;
  const cur = preview?.currency || 'usd';

  return (
    <div className={styles.modalOverlay} onClick={() => { if (!upgrading) onClose?.(); }}>
      <div className={styles.upgradeCard} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalBody}>
          <h3 className={styles.confirmTitle}>Upgrade your membership</h3>
          <p className={styles.confirmText}>
            You&apos;re on
            {' '}
            <strong>{m.plan_name || 'your current plan'}</strong>
            {`. Pick a higher ${interval.toLowerCase()} plan — you'll only pay the prorated difference now, credited for the time left on your current plan.`}
          </p>

          {loadingPlans ? (
            <p className={styles.stateText}>Loading plans…</p>
          ) : plans.length === 0 ? (
            <p className={styles.stateText}>No upgrade options available right now.</p>
          ) : (
            <div className={styles.upgradeList}>
              {plans.map((opt) => {
                const active = selected?.pricing.id === opt.pricing.id;
                return (
                  <button
                    key={opt.pricing.id}
                    type="button"
                    className={`${styles.upgradeOption} ${active ? styles.upgradeOptionActive : ''}`}
                    onClick={() => choosePlan(opt)}
                    disabled={upgrading}
                  >
                    <span className={`${styles.upgradeRadio} ${active ? styles.upgradeRadioOn : ''}`} aria-hidden="true" />
                    <span className={styles.upgradeOptName}>{opt.plan.name}</span>
                    <span className={styles.upgradeOptPrice}>
                      {money(opt.pricing.price_cents, cur)}
                      <span className={styles.upgradeOptPer}>{`/${interval}`}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {previewing && <p className={styles.stateText}>Calculating proration…</p>}
          {preview && !previewing && (
            <div className={styles.previewBox}>
              <div className={styles.previewRow}>
                <span>{`New plan (${preview.plan_name}, ${interval})`}</span>
                <span>{money(preview.new_plan_amount_cents, cur)}</span>
              </div>
              <div className={styles.previewRow}>
                <span>Credit for current plan</span>
                <span className={styles.previewCredit}>{`−${money(preview.proration_credit_cents, cur)}`}</span>
              </div>
              <div className={`${styles.previewRow} ${styles.previewTotal}`}>
                <span>Due now</span>
                <span>{money(preview.amount_due_now_cents, cur)}</span>
              </div>
              {m.discount && (
                <p className={styles.previewPromo}>
                  {`Promo ${m.discount.code || m.discount.name || ''} applied${discountValue(m.discount, cur) ? ` (${discountValue(m.discount, cur)})` : ''}${preview.amount_due_now_cents === 0 ? ' — nothing due now.' : '.'}`}
                </p>
              )}
              {preview.next_renewal_date && (() => {
                const renews = renewalCents(preview.new_plan_amount_cents, m.discount);
                return (
                  <p className={styles.previewNote}>
                    {renews === 0
                      ? `Then free on ${formatDate(preview.next_renewal_date)} while your promo is active.`
                      : `Then ${money(renews, cur)} on ${formatDate(preview.next_renewal_date)}.`}
                  </p>
                );
              })()}
            </div>
          )}

          {preview && !previewing && (
            <div className={styles.payline}>
              <span className={styles.paylineCard} aria-hidden="true" />
              <span>
                {preview.amount_due_now_cents > 0
                  ? `${card ? cardLabel(card) : 'Your card on file'} will be charged`
                  : `No charge today${card ? ` · ${cardLabel(card)} on file` : ''}`}
              </span>
            </div>
          )}

          {error && <p className={styles.coError}>{error}</p>}

          <div className={styles.confirmActions}>
            <button type="button" className={styles.coSecondary} onClick={onClose} disabled={upgrading}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.gateModalBtn}
              onClick={confirmUpgrade}
              disabled={!selected || !preview || previewing || upgrading}
            >
              {(() => {
                if (upgrading) return 'Upgrading…';
                if (!preview) return 'Upgrade';
                return preview.amount_due_now_cents > 0
                  ? `Pay ${money(preview.amount_due_now_cents, cur)} & upgrade`
                  : 'Confirm upgrade';
              })()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipUpgradeModal;
